"use client";

import { useCallback, useRef, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import type {
  ManualLogSheetView,
  ManualLogSport,
} from "@/app/_components/companion/manual-log-state";
import { MANUAL_SESSION_CONSENT_VERSION } from "@/lib/companion/session-provenance";
import { trackEvent } from "@/lib/analytics";

type ManualLogStatus = "idle" | "saving" | "saved" | "error" | "auth";

type ManualLogSheetProps = {
  view: ManualLogSheetView;
  title: string;
  body: string;
  id?: string;
  className?: string;
  triggerLabel?: "Log a day";
  readOnly?: boolean;
};

export function ManualLogSheet({
  view,
  title,
  body,
  id,
  className = "rounded-sm border border-border-light bg-warm-bg px-3 py-3",
  triggerLabel,
  readOnly = false,
}: ManualLogSheetProps) {
  const [open, setOpen] = useState(!triggerLabel);
  const [status, setStatus] = useState<ManualLogStatus>("idle");
  const requestRef = useRef<{ body: string; id: string } | null>(null);
  const pendingRef = useRef(false);
  const router = useRouter();
  const formId = id ? `${id}-form` : undefined;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (readOnly || pendingRef.current || status === "saved") return;
      const formData = new FormData(event.currentTarget);
      const sport = parseManualLogSport(formData.get("sport")) ?? view.defaultSport;
      const placeSlug = stringFormValue(formData.get("placeSlug")) ?? view.defaultPlaceSlug;
      const place =
        view.placeOptions.find((option) => option.slug === placeSlug) ?? view.placeOptions[0];
      const sessionDate = stringFormValue(formData.get("sessionDate")) ?? view.defaultDate;
      const durationText = stringFormValue(formData.get("durationMinutes"));
      const durationMinutes = durationText ? Number(durationText) : undefined;
      const timeZone = view.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (
        durationMinutes !== undefined &&
        (!Number.isFinite(durationMinutes) || durationMinutes <= 0)
      ) {
        setStatus("error");
        return;
      }

      const body = JSON.stringify({ sport, sessionDate, durationMinutes,
        placeLabel: place?.label ?? placeSlug, placeSlug, trail: [], segments: [],
        consentVersion: MANUAL_SESSION_CONSENT_VERSION, timeZone });
      if (requestRef.current?.body !== body) requestRef.current = { body, id: globalThis.crypto.randomUUID() };
      pendingRef.current = true;
      setStatus("saving");
      try {
      const response = await fetch("/api/companion/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: requestRef.current.id,
          sport,
          sessionDate,
          durationMinutes,
          placeLabel: place?.label ?? placeSlug,
          placeSlug,
          trail: [],
          segments: [],
          consentVersion: MANUAL_SESSION_CONSENT_VERSION,
          timeZone,
        }),
      });

      if (response.status === 401) {
        setStatus("auth");
        return;
      }
      if (!response.ok) {
        setStatus("error");
        return;
      }

      setStatus("saved");
      try {
        trackEvent("manual_log_completed", { sport });
      } catch {
        // Optional measurement must not turn an accepted save into a failure.
      }
      window.setTimeout(() => {
        router.refresh();
      }, 700);
      } catch {
        setStatus("error");
      } finally {
        pendingRef.current = false;
      }
    },
    [readOnly, router, status, view],
  );

  return (
    <div id={id} className={className}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-semibold text-navy">{title}</p>
          <p className="mt-1 text-[12px] leading-relaxed text-text-muted">{body}</p>
        </div>
        {triggerLabel ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={formId}
            onClick={() => setOpen((current) => !current)}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy hover:border-gold/50"
          >
            {triggerLabel}
          </button>
        ) : null}
      </div>

      {open ? (
        <form id={formId} className="mt-3 grid gap-3" onSubmit={handleSubmit}>
          {view.timeZone ? <p className="text-xs text-text-muted">Dates use local time in {view.timeZone.replaceAll("_", " ")}.</p> : null}
          {readOnly ? (
            <p className="rounded-sm border border-border bg-white px-3 py-2 text-[12px] leading-relaxed text-gray-700">
              This deterministic fixture cannot add anything to your record.
            </p>
          ) : null}
          <label className="grid gap-1 text-[12px] font-semibold text-navy">
            {view.sportLabel}
            <select
              name="sport"
              defaultValue={view.defaultSport}
              disabled={readOnly}
              className="min-h-[44px] rounded-sm border border-border bg-white px-3 text-[13px] text-charcoal"
            >
              {view.sportOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-[12px] font-semibold text-navy">
            {view.placeLabel}
            <select
              name="placeSlug"
              defaultValue={view.defaultPlaceSlug}
              disabled={readOnly}
              className="min-h-[44px] rounded-sm border border-border bg-white px-3 text-[13px] text-charcoal"
            >
              {view.placeOptions.map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-[12px] font-semibold text-navy">
              {view.dateLabel}
              <input
                type="date"
                name="sessionDate"
                defaultValue={view.defaultDate}
                min={view.minDate}
                max={view.maxDate}
                disabled={readOnly}
                className="min-h-[44px] rounded-sm border border-border bg-white px-3 text-[13px] text-charcoal"
              />
            </label>
            <label className="grid gap-1 text-[12px] font-semibold text-navy">
              {view.durationLabel}
              <input
                type="number"
                name="durationMinutes"
                min="1"
                max="1440"
                step="1"
                inputMode="numeric"
                placeholder="Minutes"
                disabled={readOnly}
                className="min-h-[44px] rounded-sm border border-border bg-white px-3 text-[13px] text-charcoal"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={readOnly || status === "saving" || status === "saved"}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white hover:bg-navy/90 disabled:cursor-not-allowed disabled:bg-navy/60"
          >
            {status === "saving" ? "Adding..." : view.submitLabel}
          </button>
          {status === "saved" ? (
            <p className="text-[12px] font-semibold text-navy">Added to your record.</p>
          ) : null}
          {status === "auth" ? (
            <p className="text-[12px] font-semibold text-red-700">
              Log in to add that day to your record.
            </p>
          ) : null}
          {status === "error" ? (
            <p className="text-[12px] font-semibold text-red-700">
              Could not add that day. Check the details and try again.
            </p>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

function stringFormValue(value: FormDataEntryValue | null): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function parseManualLogSport(value: FormDataEntryValue | null): ManualLogSport | null {
  const key = stringFormValue(value);
  return key === "ski" || key === "mtb" || key === "surf" || key === "coastal" ? key : null;
}
